'use client'

import { Toggle } from '@/components/ui/toggle'
import { useUserSettings } from '@/components/user-settings-context'
import { usePlatform } from '@/hooks/use-platform'

interface Shortcut {
  id: string
  label: string
  macKeys: string[]
  windowsKeys: string[]
  /** Alternative shortcut (e.g., Tab vs Cmd+]) */
  altMacKeys?: string[]
  altWindowsKeys?: string[]
  /** Whether this shortcut can be toggled on/off */
  toggleable?: boolean
  /** Settings key for this shortcut's toggle state */
  settingsKey?: 'runCode' | 'submit'
}

const GENERAL_SHORTCUTS: Shortcut[] = [
  {
    id: 'run-code',
    label: 'Run code',
    macKeys: ['⌘', "'"],
    windowsKeys: ['Ctrl', "'"],
    toggleable: true,
    settingsKey: 'runCode',
  },
  {
    id: 'submit',
    label: 'Submit',
    macKeys: ['⌘', '↵'],
    windowsKeys: ['Ctrl', 'Enter'],
    toggleable: true,
    settingsKey: 'submit',
  },
  {
    id: 'close-tab',
    label: 'Close tab',
    macKeys: ['⌥', 'W'],
    windowsKeys: ['Alt', 'W'],
  },
  {
    id: 'maximize-panel',
    label: 'Maximize / Exit Maximize Panel',
    macKeys: ['⌥', '+'],
    windowsKeys: ['Alt', '+'],
  },
  {
    id: 'full-screen',
    label: 'Enter / Exit Full Screen',
    macKeys: ['⌥', 'F'],
    windowsKeys: ['Alt', 'F'],
  },
]

const CODE_EDITOR_SHORTCUTS: Shortcut[] = [
  {
    id: 'indent',
    label: 'To indent one level',
    macKeys: ['Tab'],
    windowsKeys: ['Tab'],
    altMacKeys: ['⌘', ']'],
    altWindowsKeys: ['Ctrl', ']'],
  },
  {
    id: 'outdent',
    label: 'To indent one fewer levels',
    macKeys: ['⇧', 'Tab'],
    windowsKeys: ['Shift', 'Tab'],
    altMacKeys: ['⌘', '['],
    altWindowsKeys: ['Ctrl', '['],
  },
  {
    id: 'move-lines',
    label: 'To move lines up / down',
    macKeys: ['⌥', 'Up/Down'],
    windowsKeys: ['Alt', 'Up/Down'],
  },
  {
    id: 'delete-line',
    label: 'Delete line and copy to buffer',
    macKeys: ['⌘', 'X'],
    windowsKeys: ['Ctrl', 'X'],
  },
  {
    id: 'comment',
    label: 'Comment / uncomment current selection',
    macKeys: ['⌘', '/'],
    windowsKeys: ['Ctrl', '/'],
  },
  {
    id: 'undo',
    label: 'Undo action',
    macKeys: ['⌘', 'Z'],
    windowsKeys: ['Ctrl', 'Z'],
  },
  {
    id: 'redo',
    label: 'Redo action',
    macKeys: ['⌘', '⇧', 'Z'],
    windowsKeys: ['Ctrl', 'Shift', 'Z'],
  },
]

interface KeyBadgeProps {
  children: React.ReactNode
}

function KeyBadge({ children }: KeyBadgeProps) {
  return (
    <kbd
      className="bg-muted text-foreground inline-flex min-w-[2rem] items-center justify-center rounded px-2.5 py-1.5 text-sm font-medium"
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
      }}
    >
      {children}
    </kbd>
  )
}

interface ShortcutRowProps {
  shortcut: Shortcut
  isMac: boolean
  enabled?: boolean
  onToggle?: (enabled: boolean) => void
}

function ShortcutRow({ shortcut, isMac, enabled, onToggle }: ShortcutRowProps) {
  const keys = isMac ? shortcut.macKeys : shortcut.windowsKeys
  const altKeys = isMac ? shortcut.altMacKeys : shortcut.altWindowsKeys

  return (
    <div className="flex items-center justify-between py-3">
      <span className="text-foreground text-sm">{shortcut.label}</span>
      <div className="flex items-center gap-3">
        {shortcut.toggleable && onToggle && (
          <Toggle
            checked={enabled ?? true}
            onChange={onToggle}
            aria-label={`Toggle ${shortcut.label} shortcut`}
          />
        )}
        <div className="flex items-center gap-1">
          {keys.map((key, i) => (
            <KeyBadge key={`${shortcut.id}-key-${i}`}>{key}</KeyBadge>
          ))}
          {altKeys && (
            <>
              <span className="text-muted-foreground mx-1.5 text-sm">or</span>
              {altKeys.map((key, i) => (
                <KeyBadge key={`${shortcut.id}-alt-${i}`}>{key}</KeyBadge>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

interface ShortcutGroupProps {
  title: string
  shortcuts: Shortcut[]
  isMac: boolean
  toggledShortcuts: Record<string, boolean>
  onToggleShortcut: (id: string, enabled: boolean) => void
}

function ShortcutGroup({
  title,
  shortcuts,
  isMac,
  toggledShortcuts,
  onToggleShortcut,
}: ShortcutGroupProps) {
  return (
    <div className="space-y-1">
      <h3 className="text-muted-foreground pb-2 text-xs font-semibold uppercase tracking-wider">
        {title}
      </h3>
      <div className="divide-border divide-y">
        {shortcuts.map((shortcut) => (
          <ShortcutRow
            key={shortcut.id}
            shortcut={shortcut}
            isMac={isMac}
            enabled={toggledShortcuts[shortcut.id] ?? true}
            onToggle={
              shortcut.toggleable
                ? (enabled) => onToggleShortcut(shortcut.id, enabled)
                : undefined
            }
          />
        ))}
      </div>
    </div>
  )
}

export function ShortcutsSettings() {
  const { isMac } = usePlatform()
  const { settings, updateSettings } = useUserSettings()

  // Build toggled shortcuts map from settings
  const toggledShortcuts: Record<string, boolean> = {
    'run-code': settings.shortcuts.runCode,
    submit: settings.shortcuts.submit,
  }

  const handleToggleShortcut = (id: string, enabled: boolean) => {
    // Map shortcut IDs to settings keys
    const shortcut = [...GENERAL_SHORTCUTS, ...CODE_EDITOR_SHORTCUTS].find(
      (s) => s.id === id,
    )
    if (shortcut?.settingsKey) {
      updateSettings({ shortcuts: { [shortcut.settingsKey]: enabled } })
    }
  }

  // Show loading state during SSR / hydration
  if (isMac === null) {
    return (
      <div className="flex h-32 items-center justify-center">
        <span className="text-muted-foreground text-sm">
          Loading shortcuts...
        </span>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <ShortcutGroup
        title="General"
        shortcuts={GENERAL_SHORTCUTS}
        isMac={isMac}
        toggledShortcuts={toggledShortcuts}
        onToggleShortcut={handleToggleShortcut}
      />
      <ShortcutGroup
        title="Code Editor"
        shortcuts={CODE_EDITOR_SHORTCUTS}
        isMac={isMac}
        toggledShortcuts={toggledShortcuts}
        onToggleShortcut={handleToggleShortcut}
      />
    </div>
  )
}
